import React from 'react';
import Navbar from '../components/Navbar';
import ContactForm from '../components/ContactForm';
import { APP_URL } from '../lib/appUrl';

/**
 * /support — the URL declared as the App Store support URL and the Play Store
 * support site. It has to stand on its own for someone who has never opened
 * the app and has no account, which is exactly the case for a store reviewer.
 *
 * That used to mean printing an address. Contact is form-only now, so the page
 * carries the form itself rather than pointing anywhere else: a reviewer, a
 * guest ticket buyer with no account, and a suspended user who cannot sign in
 * can all write to us from here without leaving the page.
 */
export default function Support() {
    return (
        <div className="min-h-screen grain scanlines">
            <Navbar />

            <section className="border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-12 lg:pt-28 lg:pb-16">
                    <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-6 fade-up">
                        // Support
                    </p>
                    <h1
                        className="font-display uppercase tracking-tight leading-[0.85] text-white fade-up"
                        data-testid="support-title"
                    >
                        <span className="block text-6xl sm:text-8xl lg:text-9xl">Need a</span>
                        <span className="block text-6xl sm:text-8xl lg:text-9xl text-neon neon-glow">hand?</span>
                    </h1>
                </div>
            </section>

            <section className="border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
                    {/* LEFT — the form, first and unmissable */}
                    <div className="lg:col-span-7">
                        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-neon mb-4">
                            // Write to us
                        </p>
                        <h2 className="font-display uppercase text-3xl tracking-tight leading-[0.95] mb-6">
                            One form,<br />a <span className="text-neon">real person.</span>
                        </h2>
                        <p className="text-white/70 leading-relaxed text-sm mb-8">
                            No account needed. Send this any time and we reply within one business day. If you
                            bought a ticket as a guest and never made an account, or you cannot sign in at all,
                            this is the way to reach us.
                        </p>

                        <ContactForm defaultCategory="tickets" testIdPrefix="support" />
                    </div>

                    {/* RIGHT — routing, so the inbox gets the right things */}
                    <div className="lg:col-span-5 space-y-4 text-sm">
                        <div className="border-t border-white/10 pt-4">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon mb-1">
                                // Refunds
                            </p>
                            <p className="text-white/60 leading-relaxed">
                                Portal does not process refunds.{' '}
                                <span className="text-white">Only the event organizer can issue a refund</span>{' '}
                                for their event, under their own refund policy. Open your Event Ticket in your
                                Portal wallet and use the &ldquo;Contact organizer&rdquo; option.
                            </p>
                        </div>

                        <div className="border-t border-white/10 pt-4">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon mb-1">
                                // A specific Event Ticket
                            </p>
                            <p className="text-white/60 leading-relaxed">
                                Entry problems, transfers, and lost QR codes are handled by the organizer of
                                your event, from inside your wallet. They can see the door list, and we cannot
                                admit you on their behalf.
                            </p>
                        </div>

                        <div className="border-t border-white/10 pt-4">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon mb-1">
                                // Organizers
                            </p>
                            <a
                                href={APP_URL}
                                className="text-white/80 hover:text-neon underline underline-offset-4 decoration-dotted"
                            >
                                Use Support inside the producer console
                            </a>
                        </div>

                        <div className="border-t border-white/10 pt-4">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon mb-1">
                                // Account and privacy
                            </p>
                            <p className="text-white/60 leading-relaxed">
                                To delete your account, or to request a copy of your data, send the form with
                                &ldquo;Privacy or data request&rdquo; selected. You do not need to be signed in.
                            </p>
                        </div>

                        <div className="border-t border-white/10 pt-4">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon mb-1">
                                // Suspended accounts
                            </p>
                            <p className="text-white/60 leading-relaxed">
                                If you cannot sign in to appeal a suspension, send the form with &ldquo;Appeal a
                                suspension&rdquo; selected and it reaches the same queue.
                            </p>
                        </div>

                        <div className="border-t border-white/10 pt-4">
                            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neon mb-1">
                                // What happens next
                            </p>
                            <p className="text-white/60 leading-relaxed">
                                You get an email confirming we have it, with a reference number. Quote that
                                reference if you write to us again about the same thing.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section>
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                        Bankroll Collective LLC · Portal
                    </p>
                </div>
            </section>
        </div>
    );
}
